import type { InjectionKey } from 'vue'
import type { useMacroRepository } from './composables/useMacroRepository'
import type { Macro } from '@rapoo/device-sdk'

/**
 * 宏播放模式：
 * 1 = 指定循环次数
 * 2 = 使用指派按键切换开关
 * 3 = 按下时播放
 */
export type MacroPlayMode = 1 | 2 | 3

/**
 * 宏仓储 hook 类型，允许外部（鼠标/键盘）注入不同实现共享同一个 TabMacro 组件。
 */
export type UseMacroRepository = typeof useMacroRepository

/**
 * 通过 provide/inject 替换默认的宏仓储实现。
 * 不提供时使用 keyboard 默认仓储（基于 keyboardKeys feature）。
 */
export const MacroRepositorySymbol: InjectionKey<UseMacroRepository> =
  Symbol('macro-repository-hook')

/**
 * 编辑器侧的记录状态（用于草稿/撤销管理）。
 */
export type MacroRecordStatus = 'original' | 'modified' | 'added' | 'deleted'

/**
 * SDK 中的鼠标按键码。
 */
export const MOUSE_KEY_CODE = {
  left: 0xf0,
  right: 0xf1,
  middle: 0xf2,
  back: 0xf3,
  forward: 0xf4,
} as const

/**
 * SDK 中用于坐标动作的特殊键码。
 */
export const COORDINATE_KEY_CODE = 0xf7

/**
 * 设备支持记录的滚轮方向类型。
 * - `'vertical'`：垂直滚轮
 * - `'horizontal'`：水平滚轮
 */
export type WheelType = 'vertical' | 'horizontal'

/**
 * 四个方向的滚轮共用同一个keycode，通过vWheel和hWheel区分方向
 */
export const WHEEL_KEY_CODE = 0xf8

/** SDK loop value for toggle mode (PlayType 2). */
export const LOOP_MODE_TOGGLE = 0xfffe
/** SDK loop value for hold mode (PlayType 3). */
export const LOOP_MODE_HOLD = 0xffff
/** Maximum valid loop count for PlayType 1. */
export const LOOP_MAX = 0xfffd

/**
 * 编辑器中的播放方法结构。
 */
export interface MacroEditorMethod {
  num: number
  index: MacroPlayMode
  ignoreTime: boolean
}

/**
 * 左侧宏列表项（与 SDK Macro 的 name/index 保持同结构）。
 */
export interface MacroListItem {
  name: string
  index: number
  hasContent?: boolean
}

interface MacroEditorRecordBase {
  __id?: string
  __status?: MacroRecordStatus
}

export interface MacroWheelDirection {
  vWheel: number // -1 后滚 / 1 前滚
  hWheel: number // -1 右滚 / 1 左滚
}

/**
 * type=1：键盘/鼠标按键动作。
 */
export interface MacroEditorKeyAction extends MacroEditorRecordBase {
  type: 1
  keystroke: number
  action: 0 | 1
}

/**
 * type=2：坐标动作。
 */
export interface MacroEditorCoordinateAction extends MacroEditorRecordBase {
  type: 2
  x_coordinate: number
  y_coordinate: number
}

/**
 * type=3：滚轮动作。
 */
export interface MacroEditorWheelAction extends MacroEditorRecordBase {
  type: 3
  vWheel: number
  hWheel: number
}

/**
 * type=4：延迟动作。
 */
export interface MacroEditorDelayAction extends MacroEditorRecordBase {
  type: 4
  time: number
}

/** 延迟动作可用时长范围（ms）。 */
export const MACRO_DELAY_MIN = 2
export const MACRO_DELAY_MAX = 30000

/** 手动新增延迟时的默认时长（ms）。 */
export const MACRO_DELAY_DEFAULT = 20

export type MacroEditorAction =
  | MacroEditorKeyAction
  | MacroEditorCoordinateAction
  | MacroEditorWheelAction
  | MacroEditorDelayAction

/**
 * 右侧编辑器的完整数据结构。
 */
export interface MacroEditorData {
  index: number
  data: MacroEditorAction[]
  method: MacroEditorMethod
}

/** 缓存中的宏记录结构。 */
export interface StoredMacroItem {
  macro: Macro
  updatedAt: number
}
